import englishBundle from './localized-content-en.json';
import japaneseBundle from './localized-content-jp.json';

import englishHeaderBundle from './header-localized-content-en.json';
import japaneseHeaderBundle from './header-localized-content-jp.json';
import englishFooterBundle from './footer-localized-content-en.json';
import japaneseFooterBundle from './footer-localized-content-jp.json';

import log, {API_CALL_TIMEOUT, JAHIA_CONTENT_API_URL} from "../cfg";
import timeoutPromise, {COGNITO_SSO_TOKEN, handleError} from "./service.utils";
import axios, {AxiosResponse} from "axios";
// @ts-ignore
import sessionStorage from "store/storages/sessionStorage";
import {Guid} from "guid-typescript";

class LocalizationService {
    static SELECTED_LANG: string = 'Selected-Lang';
    static EN: string = 'lang-english-us';
    static JP: string = 'lang-japanese';
    static CONTRACT_TYPES = "contractTypes";
    static USAGE_TYPES = "usageTypes";
    static TRANSACTION_TYPES = "transactionTypes";

    getStaticHeaderContentBundle(lang: string): Promise<any> {
        if (lang === LocalizationService.EN) {
            return new Promise<any>((resolve, reject) => {
                setTimeout(() => {
                    resolve(englishHeaderBundle);
                }, 2000);
            });
        } else if (lang === LocalizationService.JP) {
            return new Promise<any>((resolve, reject) => {
                setTimeout(() => {
                    resolve(japaneseHeaderBundle);
                }, 2000);
            });
        }
        return Promise.reject({"message": "Unsupported language '" + lang + "' selected!"});
    };

    getStaticFooterContentBundle(lang: string): Promise<any> {
        if (lang === LocalizationService.EN) {
            return new Promise<any>((resolve, reject) => {
                setTimeout(() => {
                    resolve(englishFooterBundle);
                }, 2000);
            });
        } else if (lang === LocalizationService.JP) {
            return new Promise<any>((resolve, reject) => {
                setTimeout(() => {
                    resolve(japaneseFooterBundle);
                }, 2000);
            });
        }
        return Promise.reject({"message": "Unsupported language '" + lang + "' selected!"});
    };

    getStaticContentBundle(lang: string): Promise<any> {
        if (lang === LocalizationService.EN) {
            return new Promise<any>((resolve, reject) => {
                setTimeout(() => {
                    resolve(englishBundle);
                }, 2000);
            });
        } else if (lang === LocalizationService.JP) {
            return new Promise<any>((resolve, reject) => {
                setTimeout(() => {
                    resolve(japaneseBundle);
                }, 2000);
            });
        }
        return Promise.reject({"message": "Unsupported language '" + lang + "' selected!"});
    };

    getJahiaContentBundle(lang: string): Promise<any> {
        const cognitoSsoToken = sessionStorage.read(COGNITO_SSO_TOKEN);
        if (!cognitoSsoToken || cognitoSsoToken === 'null' || cognitoSsoToken === 'undefined') {
            return Promise.reject({ code: 401});
        }
        const apiUrl = JAHIA_CONTENT_API_URL;

        if (process.env.REACT_APP_USE_FAKE_JAHIA === 'TRUE') {
            return this.getStaticContentBundle(lang);
        }

        if (lang === LocalizationService.EN || lang === LocalizationService.JP) {
            log.debug(`Calling JAHIA content API: ${apiUrl}`);
            const to = API_CALL_TIMEOUT ? parseInt(API_CALL_TIMEOUT) : 20000;

            return timeoutPromise(to, "loading Jahia content",
                axios.get(apiUrl, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${cognitoSsoToken}`,
                        'X-correlation-id': Guid.create().toString().replace('-', ''),
                        'X-Owner-Id' : -1
                    }
                })
                    .then((resp: AxiosResponse<any>) => {
                            return this.mappingOfJahiaKeysToLocalizationKeys(resp.data);
                        }
                    )
                    .catch((error: any) => handleError(error, apiUrl)));
        }
        return Promise.reject({"message": "Unsupported language '" + lang + "' selected!"});
    };

    mappingOfJahiaKeysToLocalizationKeys(data: any) {
        let newLocalizationContent: any = {};
        let contractTypesContent: any = {};
        let usageTypesContent: any = {};
        let transactionTypesContent: any = {};

        Object.keys(data).forEach(key => {
            if (key.indexOf('.contract-types.') > 0) {
                const keyParts: string[] = key.split(".");
                const lastKeyPart = keyParts[keyParts.length - 1];
                contractTypesContent[lastKeyPart] = data[key];
            } else if (key.indexOf('.usage-types.') > 0) {
                const keyParts: string[] = key.split(".");
                const lastKeyPart = keyParts[keyParts.length - 1];
                usageTypesContent[lastKeyPart] = data[key];
            }
            else if (key.indexOf('.Transaction Types.') > 0) {
                const keyParts: string[] = key.split(".");
                const lastKeyPart = keyParts[keyParts.length - 1];
                transactionTypesContent[lastKeyPart] = data[key];
            }
             else {
                const keyParts: string[] = key.split(".");
                const lastKeyPart = keyParts[keyParts.length - 1];
                if (typeof data[key] === 'string' || Number.isInteger(data[key])) {
                    const appKey = 'static.content.' + lastKeyPart;
                    newLocalizationContent[appKey] = data[key];
                } else {
                    const appKey = 'static.content.' + lastKeyPart + ".";
                    const subMap: any = data[key];
                    Object.keys(subMap).forEach(subKey => {
                        const resSubKey = appKey + subKey;
                        newLocalizationContent[resSubKey] = subMap[subKey];
                    });
                }
            }
        });

        sessionStorage.write(LocalizationService.CONTRACT_TYPES, JSON.stringify(contractTypesContent));
        sessionStorage.write(LocalizationService.USAGE_TYPES, JSON.stringify(usageTypesContent));
        sessionStorage.write(LocalizationService.TRANSACTION_TYPES, JSON.stringify(transactionTypesContent));
        
        return newLocalizationContent;
    }

    /**
     *  Contract Type as it is defined in JAHIA
     *  "reservation-transformation.contract-types.bali-gardens": {
            "description-in-advanced-search": "Bali Dua Nusa Gardens",
            "description-in-contract-hover": "Bali Nusa Dua Gardens"
         }
     * @param contractTypeName
     */
    getContractTypeNameFromJahia(contractTypeName: string) {
        if (contractTypeName && contractTypeName.length > 0) {
            const mapping = JSON.parse(sessionStorage.read(LocalizationService.CONTRACT_TYPES));
            const key = contractTypeName.toLowerCase().replace(/ /g, '-',);
            if (mapping[key]) {
                return mapping[key];
            }
        }

        return {"description-in-advanced-search": contractTypeName, "description-in-contract-hover": contractTypeName};
    }

    /**
     *  Usage Type as it is defined in JAHIA
     */
        getUsageTypeNameFromJahia(usageType: string): {value: string; key: string;} {
        const apiUrl = JAHIA_CONTENT_API_URL;
        
        if (apiUrl.toLowerCase().startsWith('fake')) {
            return {"value": usageType, "key": "n/a"};
        }
        
        if (usageType && usageType.length > 0) {
            const mapping = JSON.parse(sessionStorage.read(LocalizationService.USAGE_TYPES));
            const key = usageType.trim().toLowerCase().replace(/ /g, '-',);
            if (mapping[key]) {
                return mapping[key];
            }
        }

        return {"value": usageType, "key": "n/a"};
    }

    /**
     *  Transaction Type as it is defined in JAHIA
     */
    getTransactionTypeNameFromJahia(transactionType: string): {value: string; key: string;} {
        const apiUrl = JAHIA_CONTENT_API_URL;
        
        if (apiUrl.toLowerCase().startsWith('fake')) {
            return {"value": transactionType, "key": "n/a"};
        }
        
        if (transactionType && transactionType.length > 0) {
            const mapping = JSON.parse(sessionStorage.read(LocalizationService.TRANSACTION_TYPES));
            const key = transactionType.trim().toLowerCase().replace(/ /g, '-',);
            if (mapping[key]) {
                return mapping[key];
            }
        }

        return {"value": transactionType, "key": "n/a"};
    }
}

export default LocalizationService;